import React, { useEffect, useState } from 'react';
import './challenge-input-type.styles.scss';
import { useSelector } from 'react-redux';
import ToggleInput from '@/components/SHARED/toggle-input/toggle-input.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faXmark } from '@fortawesome/free-solid-svg-icons';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';

/**
 * 
 * @param {object} param0 
 * @param {function} param0.saveData
 * @param {object} param0.formData
 * @param {boolean} param0.disabled
 * @returns {JSX.Element}
 * schema:
 * opt_inputType string ('shortAnswer', 'paragraph', 'multipleChoice')
 * opt_inputOptions array (for multiple choice)
 * opt_correctAnswers array (for short answer)
 * opt_correctOptionIndex number (for multiple choice)
 * opt_autoApprove boolean (for paragraph)
 * opt_allowFileUpload boolean (for all types)
 */

const ManageChallengeInputType = ({
  saveData,
  formData,
  disabled,
  type
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const membership = useSelector(state => state.userData.membership);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [rawAnswerText, setRawAnswerText] = useState('');
  const inputType = formData?.opt_inputType || 'shortAnswer';
  const mcOptions = Array.isArray(formData?.opt_inputOptions) ? formData.opt_inputOptions : [];
  const autoApprove = !!formData?.opt_autoApprove;

  const answersKey = Array.isArray(formData?.opt_correctAnswers) ? formData.opt_correctAnswers.join('\n') : '';
  useEffect(() => {
    const fromRaw = rawAnswerText.split('\n')
    .map(a => a.trim())
    .filter(a => a.length > 0)
    .join('\n');
    if (answersKey !== fromRaw) {
      setRawAnswerText(answersKey);
    }
  }, [answersKey]);

  useEffect(() => {
    if (!formData) return;
    if (!formData.opt_inputType) {
      saveData('opt_inputType', 'shortAnswer');
    }
  }, [formData])

  useEffect(() => {
    if (!formData) return;
    if (inputType === 'paragraph' && formData.opt_autoApprove) {
      saveData('opt_autoApprove', false);
    }
    if (inputType !== 'shortAnswer' && 
      Array.isArray(formData.opt_correctAnswers) && 
      formData.opt_correctAnswers.length > 0) {
      saveData('opt_correctAnswers', []);
    }
    if (inputType !== 'multipleChoice' && 
      formData.opt_correctOptionIndex !== null && 
      formData.opt_correctOptionIndex !== undefined) {
      saveData('opt_correctOptionIndex', null);
    }
    if (inputType === 'multipleChoice' && 
      !Array.isArray(formData.opt_inputOptions)) {
      saveData('opt_inputOptions', ['']);
    }
  }, [inputType, formData])

  useEffect(() => {
    if (!formData) return;
    if (!formData.opt_autoApprove) {
      if (formData.opt_correctOptionIndex !== null && 
        formData.opt_correctOptionIndex !== undefined) {
        saveData('opt_correctOptionIndex', null);
      }
      if (Array.isArray(formData.opt_correctAnswers) && 
      formData.opt_correctAnswers.length > 0) {
        saveData('opt_correctAnswers', []);
      }
    }
  }, [formData?.opt_autoApprove])

  function updateOption(index, value) {
    const updated = [...mcOptions];
    updated[index] = value;
    saveData('opt_inputOptions', updated);
  }

  function addOption() {
    saveData('opt_inputOptions', [...mcOptions, '']);
  }

  function removeOption(index) {
    const updated = mcOptions.filter((_, i) => i !== index);
    saveData('opt_inputOptions', updated);
    const currentCorrect = formData?.opt_correctOptionIndex;
    if (currentCorrect === index) {
      saveData('opt_correctOptionIndex', null);
    } else if (typeof currentCorrect === 'number' && 
      currentCorrect > index) {
      saveData('opt_correctOptionIndex', currentCorrect - 1);
    }
  }

  function moveOption(index, direction) {
    const toIndex = direction === 'up' ? index - 1 : index + 1;
    if (toIndex < 0 || toIndex >= mcOptions.length) return;
    const updated = [...mcOptions];
    const movingValue = updated[index];
    updated[index] = updated[toIndex];
    updated[toIndex] = movingValue;
    saveData('opt_inputOptions', updated);

    const currentCorrect = formData?.opt_correctOptionIndex;
    if (currentCorrect === index) {
      saveData('opt_correctOptionIndex', toIndex);
    } else if (currentCorrect === toIndex) {
      saveData('opt_correctOptionIndex', index);
    }
  }

  function updateShortAnswerKey(value) {
    setRawAnswerText(value);
    const answers = value
      .split('\n')
      .map(a => a.trim())
      .filter(a => a.length > 0);
    saveData('opt_correctAnswers', answers);
  }

  function checkForAccessAndSave(key, value) {
    if (key === 'opt_allowFileUpload' && value === true && membership?.membership === 0) {
      setShowUpgradeModal('File uploads are a Pro/Premium feature. Please upgrade your account to enable this option.');
      return;
    }
    if (key === 'opt_autoApprove' && value === true && membership?.membership < 2) {
      setShowUpgradeModal('Auto-approval is a Premium feature. Please upgrade your account to enable this option.');
      return;
    } else if (key === 'opt_autoApprove' && value === true) {
      saveData('opt_allowFileUpload', false);
    }
    saveData(key, value);
  }

  return (
    <div className={`challenge-input-type g-list-item form-section ${disabled} ${screenSize}`}>
      <h3>Player Input Type<span className="required">*</span></h3>
      <hr />
      <p>Choose the type of input players will use to submit their challenge response.</p>

      <div className='field small'>
        <label>Response Type</label>
        <select
          value={inputType}
          onChange={(e) => saveData('opt_inputType', e.target.value)}>
          <option value='shortAnswer'>Short Answer</option>
          <option value='paragraph'>Paragraph</option>
          <option value='multipleChoice'>Multiple Choice</option>
          <option value='none'>None (e.g. for a {type.toLowerCase()} with no submission required)</option>
        </select>
      </div>

      {
        (inputType === 'multipleChoice') &&
        <>
          <div className='g-space-1'></div>
          <div className='options-panel'>
            <div className='options-head'>Possible Options</div>
            {
              (mcOptions.length === 0)
                ?
                <div className='meta'>No options added yet.</div>
                :
                mcOptions.map((option, index) => (
                  <div
                    className='option-row field small'
                    key={`mc-option-${index}`}>
                    <input
                      type='text'
                      value={option}
                      placeholder={`Option ${index + 1}`}
                      onChange={(e) => updateOption(index, e.target.value)}
                    />
                    <button
                      type='button'
                      className='g-button small-btn'
                      onClick={() => moveOption(index, 'up')}
                      disabled={index === 0}>
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button
                      type='button'
                      className='g-button small-btn'
                      onClick={() => moveOption(index, 'down')}
                      disabled={index === mcOptions.length - 1}>
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                    <button
                      type='button'
                      className='g-button small-btn red'
                      onClick={() => removeOption(index)}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ))
            }
            <div className='g-space-0-5'></div>
            <button type='button' className='g-button small-btn' onClick={() => addOption()}>
              Add Option
            </button>
          </div>
        </>
      }

      <div className='g-space-1'></div>
      <div className='toggle-with-text'>
        <ToggleInput
          contentKey='opt_allowFileUpload'
          checked={formData?.opt_allowFileUpload}
          saveData={checkForAccessAndSave}
        />
        <div className='toggle-text'>
          <b>Allow File Uploads:</b>
          <div className='meta'>Players can optionally upload a file with their response.</div>
        </div>
      </div>

      {
        (
          inputType === 'shortAnswer' || inputType === 'multipleChoice'
        ) &&
        <>
          <div className='g-space-1'></div>
          <div className='toggle-with-text'>
            <ToggleInput
              contentKey='opt_autoApprove'
              checked={autoApprove}
              saveData={checkForAccessAndSave}
            />
            <div className='toggle-text'>
              <b>Enable Auto-Approval:</b>
              <div className='meta'>If enabled, submissions can be automatically checked against your answer key.</div>
            </div>
          </div>
        </>
      }

      {
        (inputType === 'multipleChoice' && autoApprove) &&
        <>
          <div className='g-space-1'></div>
          <div className='options-panel'>
            <div className='options-head'>Select Correct Option</div>
            {
              (mcOptions.length === 0)
                ?
                <div className='meta'>Add at least one option to configure auto-grading.</div>
                :
                mcOptions.map((option, index) => (
                  <label className='correct-option-row' key={`mc-correct-${index}`}>
                    <input
                      type='radio'
                      name='correct-multiple-choice-option'
                      checked={formData?.opt_correctOptionIndex === index}
                      onChange={() => saveData('opt_correctOptionIndex', index)}
                      disabled={!option || option.trim().length === 0}
                    />
                    <span>{option && option.trim().length > 0 ? option : `(Option ${index + 1} is blank)`}</span>
                  </label>
                ))
            }
          </div>
        </>
      }

      {
        (inputType === 'shortAnswer' && autoApprove) &&
        <>
          <div className='g-space-1'></div>
          <div className='field'>
            <label className='no-flex'>
              <b>Possible Correct Answers</b><br />
              <span className='meta'>Enter one answer per line. Matching should be handled case-insensitively by grading logic.</span>
            </label>
            <textarea
              rows={5}
              placeholder={'Example:\nbanana\nbanana split\nripe banana'}
              value={rawAnswerText}
              onChange={(e) => updateShortAnswerKey(e.target.value)}
            />
          </div>
        </>
      }

      <div className='g-space-1'></div>
      <UpgradeRequiredModal
        show={!!showUpgradeModal}
        cancel={() => setShowUpgradeModal(null)}
        message={showUpgradeModal}
        closeButton={true} />
    </div>
  )
}

export default ManageChallengeInputType